import {
	ChevronLeftIcon,
	ChevronRightIcon,
	PlusIcon,
} from "@heroicons/react/24/outline";
import { addDays, format } from "date-fns";
import { useState } from "react";
import type { JourneyTaskOccurrence } from "../../../domain/track/journeyTaskOccurrenceAdapter";
import { useToday } from "../../../hooks/track/useToday";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import { TRACK_DAY_STEP } from "../../../lib/track/analyticsEvents";
import type { Event } from "../../../types/event";
import { ComingUpCard } from "../ComingUpCard";
import styles from "./ComingUpFeed.module.css";

/**
 * M011 S04 T03 — feed items can be manual events or journey-derived task
 * occurrences. The list is a single union sorted by its day key so both kinds
 * interleave by date.
 */
type FeedItem = Event | JourneyTaskOccurrence;

function isJourneyOccurrence(item: FeedItem): item is JourneyTaskOccurrence {
	return item.source === "journey";
}

function itemDayKey(item: FeedItem): string {
	return isJourneyOccurrence(item)
		? item.startIso.slice(0, 10)
		: item.dateTime.slice(0, 10);
}

function itemSortKey(item: FeedItem): string {
	return isJourneyOccurrence(item) ? item.startIso : item.dateTime;
}

interface ComingUpFeedProps {
	events: Event[] | undefined;
	/**
	 * M011 S04 T03 — journey task occurrences merged into the same feed.
	 * Items are interleaved by date; journey items use the teal accent.
	 */
	journeyOccurrences?: JourneyTaskOccurrence[];
	isLoading: boolean;
	onEventClick: (event: Event) => void;
	/**
	 * M011 S04 T03 — separate handler keeps the Event/JourneyTaskOccurrence
	 * type boundaries clean. T05 will wire this to navigate('/journey?step=...').
	 */
	onJourneyOccurrenceClick?: (occurrence: JourneyTaskOccurrence) => void;
	/** Phase 37 wiring (D-16) — TrackPage owns AddEventSheet open state. */
	onAddEvent?: () => void;
	/**
	 * 86ey0mc8v — controlled active-day offset from today. When provided, the
	 * feed's day-stepper is controlled by the parent (so a calendar day-tap can
	 * drive which day the feed shows). Omit for internal/uncontrolled stepping.
	 */
	dayOffset?: number;
	/** Called with the next offset when the stepper moves (controlled mode). */
	onDayOffsetChange?: (next: number) => void;
}

/**
 * ComingUpFeed (CUP-01..04 + M011 S07 T12 day-stepper + M011 S04 T03 journey merge).
 *
 * Renders the Coming Up section: title + (+) button (CUP-01 → Phase 37 wired
 * via `onAddEvent` prop), a day-stepper row (left chevron / centered label /
 * right chevron, M011 S07 T12 replaces Urgent/Today tabs), skeleton state
 * (CUP-04), empty state (CUP-04), and a date-sorted list of `ComingUpCard`s
 * (CUP-02, CUP-03).
 *
 * M011 S04 T03: merges journey-derived task occurrences into the same list
 * alongside manual events. Journey items use the teal accent and a separate
 * click handler; the event/journey distinction is preserved through to the
 * card so callers don't need to narrow on the inbound type.
 */
export function ComingUpFeed({
	events,
	journeyOccurrences,
	isLoading,
	onEventClick,
	onJourneyOccurrenceClick,
	onAddEvent,
	dayOffset: controlledOffset,
	onDayOffsetChange,
}: ComingUpFeedProps) {
	const { t, locale } = useClientTranslations("track");
	const { today } = useToday();
	const { logTrack } = useAnalytics();
	const [internalOffset, setInternalOffset] = useState(0);

	// Controlled when the parent supplies dayOffset (calendar day-tap drives it);
	// otherwise the stepper manages its own offset.
	const isControlled = controlledOffset !== undefined;
	const dayOffset = isControlled ? controlledOffset : internalOffset;

	const activeDay = addDays(today, dayOffset);
	const activeDayIso = format(activeDay, "yyyy-MM-dd");

	const stepDay = (direction: "prev" | "next") => {
		const delta = direction === "next" ? 1 : -1;
		const newOffset = dayOffset + delta;
		if (isControlled) {
			onDayOffsetChange?.(newOffset);
		} else {
			setInternalOffset(newOffset);
		}
		logTrack(TRACK_DAY_STEP, {
			direction,
			dayIso: format(addDays(today, newOffset), "yyyy-MM-dd"),
		});
	};

	const dayLabel =
		dayOffset === 0
			? t("comingUp.today")
			: new Intl.DateTimeFormat(locale, {
					weekday: "short",
					month: "short",
					day: "numeric",
				}).format(activeDay);

	const merged: FeedItem[] = [...(events ?? []), ...(journeyOccurrences ?? [])];
	const sortedItems = merged.sort((a, b) =>
		itemSortKey(a).localeCompare(itemSortKey(b)),
	);
	const visibleItems = sortedItems.filter(
		(item) => itemDayKey(item) === activeDayIso,
	);

	const noopJourneyClick = (_: JourneyTaskOccurrence) => {
		// T03 leaves journey click navigation to T05 — when no handler is
		// supplied we keep the button inert rather than firing onEventClick
		// (which would expect an Event shape).
	};
	const handleJourneyClick = onJourneyOccurrenceClick ?? noopJourneyClick;

	return (
		<section className={styles.section}>
			<div className={styles.header}>
				<h2 className={styles.title}>{t("comingUp.title")}</h2>
				<button
					type="button"
					className={styles.addBtn}
					aria-label={t("addEvent.ariaLabel")}
					onClick={onAddEvent}
				>
					<PlusIcon className={styles.plusIcon} width={20} height={20} />
				</button>
			</div>

			{/* M011 S07 T12: day-stepper replaces Urgent/Today tabs */}
			<div className={styles.stepper}>
				<button
					type="button"
					className={styles.stepBtn}
					aria-label={t("comingUp.stepper.prev")}
					onClick={() => stepDay("prev")}
				>
					<ChevronLeftIcon className={styles.stepIcon} />
				</button>
				<span className={styles.stepLabel}>{dayLabel}</span>
				<button
					type="button"
					className={styles.stepBtn}
					aria-label={t("comingUp.stepper.next")}
					onClick={() => stepDay("next")}
				>
					<ChevronRightIcon className={styles.stepIcon} />
				</button>
			</div>

			{isLoading && (
				<div className={styles.skeletonList} aria-busy="true">
					<div className={styles.skeletonCard} />
					<div className={styles.skeletonCard} />
					<div className={styles.skeletonCard} />
				</div>
			)}

			{!isLoading && visibleItems.length === 0 && (
				<div className={styles.emptyState}>
					<p className={styles.emptyTitle}>{t("comingUp.empty.title")}</p>
					<p className={styles.emptyBody}>{t("comingUp.empty.body")}</p>
				</div>
			)}

			{!isLoading && visibleItems.length > 0 && (
				<ul className={styles.list}>
					{visibleItems.map((item) =>
						isJourneyOccurrence(item) ? (
							<li key={item.id}>
								<ComingUpCard event={item} onClick={handleJourneyClick} />
							</li>
						) : (
							<li key={item.id}>
								<ComingUpCard event={item} onClick={onEventClick} />
							</li>
						),
					)}
				</ul>
			)}
		</section>
	);
}
